import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';
import { AGENT_PROVIDERS } from '../../config/agentProviders';

export interface AgentConfigurationFormData {
  id: string;
  agentKey: string;
  provider: string;
  modelName: string;
  temperature: number;
  maxTokens: number;
  maxOutputTokens: number | null;
  maxRetries: number;
  topP: number | null;
  description: string;
  isEnabled: boolean;
}

interface EditAgentConfigurationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AgentConfigurationFormData) => void;
  saving: boolean;
  saveError: string | null;
}

const EditAgentConfigurationModal: React.FC<EditAgentConfigurationModalProps> = ({
  open,
  onClose,
  onSave,
  saving,
  saveError,
}) => {
  const { selectedAgent, loading } = useAppSelector((state) => state.agentConfigurations);

  const [formData, setFormData] = useState({
    provider: '',
    modelName: '',
    temperature: 0,
    maxTokens: 0,
    maxOutputTokens: null as number | null,
    maxRetries: 0,
    topP: null as number | null,
    description: '',
    isEnabled: false,
  });

  useEffect(() => {
    if (selectedAgent) {
      setFormData({
        provider: selectedAgent.provider || '',
        modelName: selectedAgent.modelName || '',
        temperature: selectedAgent.temperature ?? 0,
        maxTokens: selectedAgent.maxTokens ?? 0,
        maxOutputTokens: selectedAgent.maxOutputTokens,
        maxRetries: selectedAgent.maxRetries ?? 0,
        topP: selectedAgent.topP,
        description: selectedAgent.description || '',
        isEnabled: selectedAgent.isEnabled || false,
      });
    }
  }, [selectedAgent, open]);

  const handleChange = (field: string, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!selectedAgent) return;

    const data: AgentConfigurationFormData = {
      id: selectedAgent.id,
      agentKey: selectedAgent.agentKey,
      provider: formData.provider,
      modelName: formData.modelName,
      temperature: formData.temperature,
      maxTokens: formData.maxTokens,
      maxOutputTokens: formData.maxOutputTokens,
      maxRetries: formData.maxRetries,
      topP: formData.topP,
      description: formData.description,
      isEnabled: formData.isEnabled,
    };

    onSave(data);
  };

  const isFormValid = formData.provider && formData.modelName;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Editar Configuración de Agente</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}

            <TextField
              label="Clave del Agente"
              value={selectedAgent?.agentKey || ''}
              fullWidth
              disabled
            />

            <Autocomplete
              options={[...AGENT_PROVIDERS]}
              value={formData.provider || null}
              onChange={(_event, value) => handleChange('provider', value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proveedor"
                  required
                />
              )}
            />

            <TextField
              label="Modelo"
              value={formData.modelName}
              onChange={(e) => handleChange('modelName', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <TextField
              label="Temperatura"
              type="number"
              value={formData.temperature}
              onChange={(e) => handleChange('temperature', parseFloat(e.target.value) || 0)}
              fullWidth
              disabled={saving}
              inputProps={{ step: 0.1, min: 0, max: 2 }}
            />

            <TextField
              label="Max Tokens"
              type="number"
              value={formData.maxTokens}
              onChange={(e) => handleChange('maxTokens', parseInt(e.target.value, 10) || 0)}
              fullWidth
              disabled={saving}
              inputProps={{ min: 0 }}
            />

            <TextField
              label="Max Output Tokens"
              type="number"
              value={formData.maxOutputTokens ?? ''}
              onChange={(e) => handleChange('maxOutputTokens', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              fullWidth
              disabled={saving}
              inputProps={{ min: 0 }}
            />

            <TextField
              label="Max Reintentos"
              type="number"
              value={formData.maxRetries}
              onChange={(e) => handleChange('maxRetries', parseInt(e.target.value, 10) || 0)}
              fullWidth
              disabled={saving}
              inputProps={{ min: 0 }}
            />

            <TextField
              label="Top P"
              type="number"
              value={formData.topP ?? ''}
              onChange={(e) => handleChange('topP', e.target.value === '' ? null : parseFloat(e.target.value))}
              fullWidth
              disabled={saving}
              inputProps={{ step: 0.1, min: 0, max: 1 }}
            />

            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={saving}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isEnabled}
                  onChange={(e) => handleChange('isEnabled', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Habilitado"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !isFormValid}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : 'Actualizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAgentConfigurationModal;
